import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CheckIcon } from '../icons';
import { outcomeCompletenessApi } from '../../utils/api';
import { fmtDate, fmtDateTime } from './dates';

// PRD-030 WU-12 — the before-signing outcome-completeness panel, built to
// the ratified mockup (rev 2, surface s0). Verdict flags any outcome it
// won't be able to confirm once delivery starts. Per-Outcome states:
// Ready to verify / Missing a detail / Can't confirm delivery. Plain
// English only — the internal check machinery is never named on screen.
//
// CTAs deep-link to the proposal Outcome editor row ("Edit outcome" /
// "Add detail" — exact copy, never a dead button); "Sign as-is — both
// sides agree" is the one-click per-side acknowledgment (F1: BOTH sides
// must acknowledge — the client gets the acknowledge action too, though
// never the edit CTAs).

const STATE = {
  ready: { label: 'Ready to verify', cls: 'text-emerald-700 bg-emerald-50 border-emerald-200', dot: 'bg-emerald-500' },
  detail: { label: 'Missing a detail', cls: 'text-amber-800 bg-amber-50 border-amber-200', dot: 'bg-amber-500' },
  blocked: { label: "Can't confirm delivery", cls: 'text-red-700 bg-red-50 border-red-200', dot: 'bg-red-500' },
};

const BLOCKING_RATINGS = ['unverifiable_as_written', 'agent_directive'];

function Chip({ state }) {
  const s = STATE[state];
  return (
    <span className={`inline-flex items-center gap-1.5 text-[12px] font-bold px-2.5 py-1 rounded-full border whitespace-nowrap ${s.cls}`}>
      <span className={`w-2 h-2 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
}

// findings carry criterion_ref `${deliverableId}:${block}` — group them by
// the Outcome that owns the deliverable.
function groupFindings(findings, rows) {
  const deliverableToOutcome = new Map(rows.map((r) => [r.deliverableId, r.outcomeId]));
  const byOutcome = new Map(rows.map((r) => [r.outcomeId, []]));
  for (const f of findings || []) {
    const deliverableId = String(f.criterion_ref || '').split(':')[0];
    const outcomeId = deliverableToOutcome.get(deliverableId);
    if (outcomeId && byOutcome.has(outcomeId)) byOutcome.get(outcomeId).push(f);
  }
  return byOutcome;
}

function outcomeState(findings) {
  if (findings.some((f) => BLOCKING_RATINGS.includes(f.rating))) return 'blocked';
  if (findings.some((f) => f.rating === 'ambiguous')) return 'detail';
  return 'ready';
}

export default function OutcomeCompletenessPanel({ proposalId, rows, isSeller, isSignedOrCancelled }) {
  const [report, setReport] = useState(null);
  const [acks, setAcks] = useState([]);
  const [current, setCurrent] = useState(true);
  const [state, setState] = useState('loading'); // loading | ready | checking | unavailable
  const [ackBusy, setAckBusy] = useState(false);
  const ranOnce = useRef(false);

  const load = useCallback(async () => {
    try {
      const res = await outcomeCompletenessApi.get(proposalId);
      setReport(res.data.report);
      setAcks(res.data.acknowledgments || []);
      setCurrent(res.data.report ? res.data.report.current : true);
      setState('ready');
      return res.data.report;
    } catch (err) {
      setState('unavailable');
      return null;
    }
  }, [proposalId]);

  const check = useCallback(async () => {
    setState('checking');
    try {
      const res = await outcomeCompletenessApi.run(proposalId);
      setReport(res.data.report);
      setAcks(res.data.acknowledgments || []);
      setCurrent(true);
      setState('ready');
    } catch (err) {
      // Rate-limited/busy → fall back to whatever report exists; a
      // provider hiccup never blocks signing (F1).
      const existing = await load();
      if (!existing) setState('unavailable');
    }
  }, [proposalId, load]);

  useEffect(() => {
    (async () => {
      const existing = await load();
      // No report yet (or stale) on an editable proposal: run the check
      // once automatically — the panel always shows a current read by
      // sign time (bounded server-side).
      if (!ranOnce.current && !isSignedOrCancelled && (!existing || !existing.current)) {
        ranOnce.current = true;
        await check();
      }
    })();
  }, [load, check, isSignedOrCancelled]);

  const jumpToOutcome = (outcomeId) => {
    const el = document.getElementById(`outcome-row-${outcomeId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.remove('prd010-flash');
      // Reflow so the animation restarts on repeat clicks.
      void el.offsetWidth; // eslint-disable-line no-void
      el.classList.add('prd010-flash');
    }
  };

  const acknowledge = async () => {
    if (!report) return;
    setAckBusy(true);
    try {
      const res = await outcomeCompletenessApi.acknowledge(proposalId, report.id);
      setAcks(res.data.acknowledgments || []);
    } catch (err) {
      await load(); // stale report etc. — reload the truth
    } finally {
      setAckBusy(false);
    }
  };

  if (state === 'loading') return null;
  if (state === 'unavailable') {
    return (
      <div className="card p-4 text-[12.5px] text-gray-600" data-testid="completeness-unavailable">
        The outcome check isn't available right now — not checked. This doesn't block signing.
      </div>
    );
  }
  if (state === 'checking' && !report) {
    return (
      <div className="card p-4 text-[13px] text-gray-600" data-testid="completeness-checking">
        Checking each outcome is described completely enough to confirm delivery…
      </div>
    );
  }
  if (!report) return null;

  const byOutcome = groupFindings(report.findings, rows);
  const states = rows.map((r) => ({ row: r, findings: byOutcome.get(r.outcomeId) || [] }))
    .map((e) => ({ ...e, state: outcomeState(e.findings) }));
  const blockedCount = states.filter((e) => e.state === 'blocked').length;
  const mySide = isSeller ? 'seller' : 'buyer';
  const myAck = acks.find((a) => a.workspace_side === mySide);
  const otherAck = acks.find((a) => a.workspace_side !== mySide);
  const sideName = (side) => (side === 'seller' ? 'Agency' : 'Client');

  return (
    <div className="space-y-3" data-testid="completeness-panel">
      {!current && (
        <div className="flex items-center gap-3 rounded-lg border border-sky-200 bg-sky-50 text-sky-800 text-[13px] px-4 py-2.5" data-testid="completeness-stale">
          <span className="flex-1">The proposal changed since the last check.</span>
          <button type="button" className="text-[12.5px] font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-lg px-3 py-1.5" onClick={check} disabled={state === 'checking'}>
            {state === 'checking' ? 'Checking…' : 'Check again'}
          </button>
        </div>
      )}

      {blockedCount > 0 ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-[13.5px] px-4 py-3">
          <b>{blockedCount} of {states.length} outcome{states.length === 1 ? '' : 's'} can't be confirmed as written.</b>{' '}
          Add the missing detail, or both sides agree to sign as-is.
        </div>
      ) : (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 text-[13.5px] px-4 py-3">
          <b>Every outcome is described completely enough to confirm delivery.</b>
        </div>
      )}

      {states.map(({ row, findings, state: s }, idx) => (
        <div key={row.outcomeId} className="card overflow-hidden" data-testid={`completeness-card-${s}`}>
          <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200 flex-wrap">
            <Chip state={s} />
            <span className="text-[15px] font-semibold text-gray-900">{row.name}</span>
            <span className="flex-1" />
            <span className="text-[11.5px] text-gray-600 tabular-nums">
              Outcome {idx + 1} of {states.length}
              {row.activeTo ? <> · due <b className="text-gray-600">{fmtDate(row.activeTo, { year: false })}</b></> : ' · no date set'}
            </span>
          </div>
          <div className="px-4 py-3.5">
            {findings.filter((f) => f.rating !== 'verifiable').map((f, i) => (
              <div
                key={i}
                className={`bg-gray-50 border border-gray-200 rounded-lg px-3.5 py-3 text-[13px] mb-2 border-l-[3px] ${
                  BLOCKING_RATINGS.includes(f.rating) ? 'border-l-red-400' : 'border-l-amber-400'
                }`}
              >
                <div className="text-gray-800">{f.comment_markdown}</div>
                {f.suggested_revision && (
                  <div className="text-emerald-700 text-[12.5px] mt-1.5">
                    Try: "{f.suggested_revision}"
                    {f.rating === 'ambiguous' && (
                      <span className="text-gray-600"> Advisory — this doesn't block signing.</span>
                    )}
                  </div>
                )}
              </div>
            ))}
            {s === 'ready' && findings.length > 0 && (
              <div className="text-[13px] text-gray-600">
                {findings.find((f) => f.rating === 'verifiable')?.comment_markdown}
              </div>
            )}

            <div className="flex gap-2.5 mt-3 flex-wrap items-center">
              {isSeller && (
                <button
                  type="button"
                  className={`text-[12.5px] font-semibold rounded-lg px-3.5 py-2 ${
                    s === 'ready'
                      ? 'border border-gray-200 text-gray-600 hover:border-gray-300'
                      : 'text-white bg-primary-600 hover:bg-primary-700'
                  }`}
                  onClick={() => jumpToOutcome(row.outcomeId)}
                >
                  {s === 'detail' ? 'Add detail' : 'Edit outcome'}
                </button>
              )}
              {s === 'blocked' && !myAck && (
                <button
                  type="button"
                  className="text-[12.5px] font-semibold border border-gray-200 text-gray-600 hover:border-gray-300 rounded-lg px-3.5 py-2 disabled:opacity-50"
                  onClick={acknowledge}
                  disabled={ackBusy || !current}
                  title={!current ? 'The proposal changed — run the check again before agreeing.' : undefined}
                >
                  {ackBusy ? 'Saving…' : 'Sign as-is — both sides agree'}
                </button>
              )}
            </div>
            {s === 'blocked' && (myAck || otherAck) && (
              <div className="text-[11.5px] text-gray-600 mt-2.5 pt-2 border-t border-gray-100">
                {[myAck, otherAck].filter(Boolean).map((a) => (
                  <div key={a.workspace_side}>
                    <span className="text-emerald-600 inline-flex align-middle mr-1"><CheckIcon /></span>
                    {sideName(a.workspace_side)} agreed to sign as-is · {fmtDateTime(a.created_at)}
                  </div>
                ))}
                {!(myAck && otherAck) && <div>Waiting on the other side.</div>}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
